import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { SportType } from '@/types';
import { getSportDisplayName } from '@/utils/sportUtils';

interface BreadcrumbItem {
  label: string;
  href: string;
  current?: boolean;
}

interface BreadcrumbNavProps {
  sport?: SportType;
  competitionName?: string;
}

// Helper to capitalize words
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const BreadcrumbNav: React.FC<BreadcrumbNavProps> = ({ 
  sport, 
  competitionName 
}) => {
  const location = useLocation();
  const params = useParams<{ sport?: string; competition?: string; postId?: string; }>();
  const pathnames = location.pathname.split('/').filter(x => x);

  if (pathnames.length === 0 && location.pathname !== '/') return null; // Should not happen on valid routes

  const breadcrumbs = pathnames.map((value, index) => {
    const to = `/${pathnames.slice(0, index + 1).join('/')}`;
    let name = capitalize(value);

    // Determine if the current breadcrumb is the "Sport" segment and not the last item
    const isSportSegment = value.toLowerCase() === 'sport' && index < pathnames.length - 1;

    // Check if this URL segment matches a sport name and use proper display name
    const sportNames: SportType[] = ['nba', 'nfl', 'mlb', 'nhl', 'soccer'];
    if (sportNames.includes(value.toLowerCase() as SportType)) {
      name = getSportDisplayName(value.toLowerCase() as SportType);
    } else if (params.sport && value.toLowerCase() === params.sport.toLowerCase()) {
      name = sport ? getSportDisplayName(sport) : capitalize(params.sport);
    }
    if (params.competition && value.toLowerCase() === params.competition.toLowerCase()) {
      name = competitionName ? competitionName : capitalize(params.competition);
    }
    // Add more specific name handling if needed (e.g., for blog post titles from postId)

    return {
      name,
      to,
      isLast: index === pathnames.length - 1,
      isSportSegment,
    };
  });

  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol 
        className="flex items-center space-x-2 text-sm text-muted-foreground"
        itemScope 
        itemType="https://schema.org/BreadcrumbList"
      >
        <li 
          className="flex items-center"
          itemProp="itemListElement"
          itemScope
          itemType="https://schema.org/ListItem"
        >
          <Link
            to="/"
            className="hover:text-foreground transition-colors"
            itemProp="item"
          >
            <span itemProp="name">
              <span className="flex items-center gap-1">
                <Home className="h-4 w-4" />
                <span className="sr-only sm:not-sr-only">Home</span>
              </span>
            </span>
          </Link>
          
          {/* Hidden schema.org position data */}
          <meta itemProp="position" content="1" />
        </li>
        {breadcrumbs.map((crumb, index) => (
          <li 
            key={crumb.to}
            className="flex items-center"
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            {/* Add separator before each breadcrumb item */}
            <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground/50" />
            
            {crumb.isLast || crumb.isSportSegment ? (
              <span 
                className={`font-medium ${crumb.isLast ? 'text-foreground' : 'text-muted-foreground'} ${crumb.isSportSegment ? 'cursor-default' : ''}`}
                itemProp="name"
                aria-current={crumb.isLast ? "page" : undefined}
              >
                {crumb.name}
              </span>
            ) : (
              <Link
                to={crumb.to}
                className="hover:text-foreground transition-colors"
                itemProp="item"
              >
                <span itemProp="name">
                  {crumb.name}
                </span>
              </Link>
            )}
            
            {/* Hidden schema.org position data */}
            <meta itemProp="position" content={String(index + 2)} />
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default BreadcrumbNav; 